// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Dashboard Page
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Building2, Clock, ChevronRight, Trash2, LayoutGrid, FolderOpen } from 'lucide-react';

import { Card, CardHeader, CardBody, EmptyState, Modal, Input, Select, Textarea, Badge, Spinner } from '@/components/ui/index';
import { Button } from '@/components/ui/Button';
import { useProjects, useCreateProject, useDeleteProject } from '@/hooks/useQueries';
import { useAppStore } from '@/store';
import { fmtDate, fmtShort, ASSET_TYPE_LABELS, truncate } from '@/lib/utils';
import type { CreateProjectRequest } from '@lcca-nexus/shared';

// ── New Project Form ──────────────────────────────────────────────────────────

const schema = z.object({
  name:          z.string().min(1, 'Name is required').max(200),
  description:   z.string().max(500).optional(),
  courseSection: z.string().max(100).optional(),
  instructor:    z.string().max(200).optional(),
  studentName:   z.string().max(200).optional(),
  studentId:     z.string().max(50).optional(),
  assetType:     z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function NewProjectModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });
  const createProject = useCreateProject();
  const navigate = useNavigate();

  const onSubmit = async (values: FormValues) => {
    const project = await createProject.mutateAsync(values as CreateProjectRequest);
    reset();
    onClose();
    navigate(`/projects/${project.id}`);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New LCCA Project"
      description="Create a new life-cycle cost analysis project"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={createProject.isPending} onClick={handleSubmit(onSubmit)}>
            Create Project
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Project / Asset Name" required error={errors.name?.message} placeholder="e.g. HVAC System – Block A" {...register('name')} />

        <div className="grid grid-cols-2 gap-3">
          <Input label="Course Section" placeholder="e.g. FM-301-A" {...register('courseSection')} />
          <Input label="Instructor" placeholder="e.g. Dr. Smith" {...register('instructor')} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Student Name" placeholder="Your full name" {...register('studentName')} />
          <Input label="Student ID" placeholder="e.g. 2021-12345" {...register('studentId')} />
        </div>

        <Select
          label="Asset Type"
          options={[
            { value: '', label: 'Select asset type…' },
            ...Object.entries(ASSET_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })),
          ]}
          {...register('assetType')}
        />

        <Textarea label="Description / Notes" placeholder="Brief description of the system or project" {...register('description')} />
      </div>
    </Modal>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export function Dashboard() {
  const [newOpen, setNewOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { data: projects, isLoading } = useProjects();
  const deleteProject = useDeleteProject();
  const navigate = useNavigate();
  const setActiveProjectId = useAppStore(s => s.setActiveProjectId);

  const handleOpen = (id: string) => {
    setActiveProjectId(id);
    navigate(`/projects/${id}`);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteProject.mutateAsync(deleteId);
    setDeleteId(null);
  };

  return (
    <div className="flex flex-col gap-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-navy-900 text-2xl leading-tight">
            My LCCA Projects
          </h2>
          <p className="text-navy-500 text-sm mt-1">
            Smart Technologies for Facility Management — Lab Exercises
          </p>
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setNewOpen(true)}>
          New Project
        </Button>
      </div>

      {/* Stats bar */}
      {projects && projects.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Projects', value: String(projects.length) },
            { label: 'Asset Types', value: String(new Set(projects.map(p => p.assetType).filter(Boolean)).size) },
            { label: 'Latest Update', value: projects[0] ? fmtDate(projects[0].updatedAt) : '—' },
          ].map(stat => (
            <Card key={stat.label} className="px-4 py-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-navy-500 font-display uppercase tracking-wider">{stat.label}</span>
              <span className="font-mono font-semibold text-navy-900 text-sm">{stat.value}</span>
            </Card>
          ))}
        </div>
      )}

      {/* Projects grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20 gap-3 text-navy-400">
          <Spinner size={24} />
          <span className="text-sm font-medium">Loading projects…</span>
        </div>
      ) : !projects?.length ? (
        <EmptyState
          icon={<FolderOpen size={32} />}
          title="No projects yet"
          description="Create your first LCCA project to start the lab exercise. Each project can hold multiple scenarios for comparison."
          action={
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setNewOpen(true)}>
              Create First Project
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map(p => (
            <Card
              key={p.id}
              className="cursor-pointer hover:-translate-y-1 hover:shadow-card-lg transition-all duration-200 group"
              onClick={() => handleOpen(p.id)}
            >
              {/* Top accent */}
              <div className="h-1 bg-gradient-to-r from-navy-700 to-amber-600 rounded-t-xl" />

              <CardBody>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-navy-50 flex items-center justify-center text-navy-600 flex-shrink-0">
                    <Building2 size={20} />
                  </div>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    {p.assetType && (
                      <Badge variant="navy">{ASSET_TYPE_LABELS[p.assetType] ?? p.assetType}</Badge>
                    )}
                  </div>
                </div>

                <h3 className="font-display font-bold text-navy-900 text-base leading-snug mb-1 group-hover:text-navy-600 transition-colors">
                  {p.name}
                </h3>

                {p.description && (
                  <p className="text-xs text-navy-500 mb-3 leading-relaxed">
                    {truncate(p.description, 80)}
                  </p>
                )}

                <div className="flex flex-col gap-1.5 text-xs text-navy-400 border-t border-parchment-100 pt-3 mt-3">
                  {p.studentName && (
                    <span className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                      {p.studentName} {p.studentId && `(${p.studentId})`}
                    </span>
                  )}
                  {p.courseSection && (
                    <span className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-navy-300 flex-shrink-0" />
                      {p.courseSection} {p.instructor && `· ${p.instructor}`}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <Clock size={11} />
                    Updated {fmtDate(p.updatedAt)}
                  </span>
                </div>
              </CardBody>

              <div className="flex items-center justify-between px-5 py-3 border-t border-parchment-100 bg-parchment-50 rounded-b-xl">
                <span className="text-xs text-navy-500 font-medium">Open Project</span>
                <div className="flex items-center gap-2">
                  <button
                    className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    onClick={e => { e.stopPropagation(); setDeleteId(p.id); }}
                  >
                    <Trash2 size={13} />
                  </button>
                  <ChevronRight size={16} className="text-navy-400 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <NewProjectModal open={newOpen} onClose={() => setNewOpen(false)} />

      <Modal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete Project?"
        description="This will permanently delete the project and all its scenarios. This cannot be undone."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="danger" loading={deleteProject.isPending} onClick={handleDelete}>
              Delete Project
            </Button>
          </>
        }
      >
        <p className="text-navy-600 text-sm">
          All scenarios and calculated results will be permanently removed from the database.
        </p>
      </Modal>

    </div>
  );
}
